import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  TextField,
  Button,
  Paper,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Snackbar,
  Alert,
  Switch,
  FormControlLabel,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Save as SaveIcon,
} from '@mui/icons-material';
import { useNavigate, useParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { romaneioService, Romaneio, ProdutoRomaneio } from '../services/romaneio';

const ConferenciaRomaneioSaidaEditar: React.FC = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { id } = useParams<{ id: string }>();
  const idRomaneio = id || '';

  const [romaneio, setRomaneio] = useState<Romaneio | null>(null);
  const [produtos, setProdutos] = useState<ProdutoRomaneio[]>([]);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'error' as 'error' | 'success' });

  const { data: romaneioData, isLoading, error } = useQuery<Romaneio>({
    queryKey: ['romaneio', idRomaneio],
    queryFn: () => romaneioService.getById(idRomaneio),
    enabled: !!idRomaneio,
  });

  useEffect(() => {
    if (romaneioData) {
      setRomaneio(romaneioData);
      setProdutos(romaneioData.produtos || []);
    }
  }, [romaneioData]);

  const handleQuantidadeDevolvidaChange = (index: number, value: number) => {
    const novosProdutos = [...produtos];
    novosProdutos[index] = { ...novosProdutos[index], quantidadeDevolvida: value };
    setProdutos(novosProdutos);
  };

  const saveMutation = useMutation({
    mutationFn: async () => {
      if (!romaneio) throw new Error('Romaneio não carregado.');

      const payload = {
        Id: Number(idRomaneio),
        Produtos: produtos.map((p) => ({
          id: p.id ? Number(p.id) : 0,
          idRomaneio: Number(idRomaneio),
          codProduto: Number(p.idProduto),
          quantidadeDevolvida: Number(p.quantidadeDevolvida || 0),
        })),
      };
      return romaneioService.salvarConferenciaSaida(payload as any);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['romaneios'] });
      queryClient.invalidateQueries({ queryKey: ['romaneio', idRomaneio] });
      setSnackbar({ open: true, message: 'Conferência de saída salva com sucesso.', severity: 'success' });
      navigate('/ConferenciaRomaneioSaida');
    },
    onError: (error: any) => {
      setSnackbar({
        open: true,
        message: 'Erro ao salvar conferência de saída: ' + (error.message || error),
        severity: 'error',
      });
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    saveMutation.mutate();
  };

  const handleBack = () => {
    navigate('/ConferenciaRomaneioSaida');
  };

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <Typography>Carregando...</Typography>
      </Box>
    );
  }

  if (error || !romaneio) {
    const errorMessage = (error as any)?.response?.data?.title
      || (error as any)?.response?.data?.message
      || (error as any)?.message
      || 'Erro ao carregar o romaneio.';
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4, flexDirection: 'column', alignItems: 'center' }}>
        <Typography color="error" gutterBottom>Erro ao carregar o romaneio.</Typography>
        <Typography color="error" variant="body2">{errorMessage}</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ maxWidth: 1200, mx: 'auto', p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" component="h1">
          Editar romaneio - Itens que não ficaram em notas de saídas #{idRomaneio}
        </Typography>
        <Button variant="outlined" startIcon={<ArrowBackIcon />} onClick={handleBack}>
          Voltar
        </Button>
      </Box>

      <form onSubmit={handleSubmit}>
        <Paper sx={{ p: 3, mb: 3 }}>
          <Grid container spacing={2}>
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                fullWidth
                label="Funcionário"
                value={romaneio.funcionario || ''}
                slotProps={{ input: { readOnly: true } }}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                fullWidth
                label="Praça"
                value={romaneio.praca || ''}
                slotProps={{ input: { readOnly: true } }}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 4 }}>
              <TextField
                fullWidth
                label="Data saída"
                value={romaneio.dataSaidaString || ''}
                slotProps={{ input: { readOnly: true } }}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 4 }}>
              <TextField
                fullWidth
                label="Data retorno"
                value={romaneio.dataRetornoString || ''}
                slotProps={{ input: { readOnly: true } }}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 4 }}>
              <FormControlLabel
                control={<Switch checked={romaneio.finalizado} readOnly />}
                label={`Status: ${romaneio.finalizado ? 'FINALIZADO' : 'INICIADO'}`}
              />
            </Grid>
          </Grid>
        </Paper>

        <Paper sx={{ p: 3, mb: 3 }}>
          <Typography variant="h6" sx={{ mb: 2 }}>
            Produtos
          </Typography>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 'bold' }}>Referência</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Nome</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Quantidade retorno</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {produtos.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={3} align="center">
                      <Typography color="text.secondary">Nenhum produto encontrado.</Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  produtos.map((produto, index) => (
                    <TableRow key={produto.id || `prod-${index}`}>
                      <TableCell>{produto.referencia}</TableCell>
                      <TableCell>{produto.nomeProduto}</TableCell>
                      <TableCell>
                        <TextField
                          type="number"
                          value={produto.quantidadeDevolvida ?? 0}
                          onChange={(e) => handleQuantidadeDevolvidaChange(index, Number(e.target.value))}
                          size="small"
                          sx={{ width: 100 }}
                        />
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>

        <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
          <Button variant="outlined" onClick={handleBack} disabled={saveMutation.isPending}>
            Voltar
          </Button>
          <Button
            variant="contained"
            type="submit"
            startIcon={<SaveIcon />}
            disabled={saveMutation.isPending}
          >
            {saveMutation.isPending ? 'Salvando...' : 'Salvar'}
          </Button>
        </Box>
      </form>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert onClose={() => setSnackbar({ ...snackbar, open: false })} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default ConferenciaRomaneioSaidaEditar;
